import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, LayoutDashboard, Cog, Globe, KeyRound, Eye, EyeOff, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiErrorDetail } from "@/lib/api";

export function ChangePasswordModal({ onClose }) {
  const { t } = useLang();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) { toast.error(t("cp.min")); return; }
    if (newPassword !== confirmPassword) { toast.error(t("cp.mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/client/change-password", { new_password: newPassword, confirm_password: confirmPassword });
      toast.success(t("cp.success"));
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="change-password-modal">
      <div className="absolute inset-0 bg-sapphire/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-strong rounded-2xl w-full max-w-md p-6 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-gold" /> {t("cp.title")}
          </h3>
          <button onClick={onClose} data-testid="cp-close-btn" className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form className="space-y-3" onSubmit={submit}>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono block mb-1.5">{t("cp.new")}</span>
            <input data-testid="cp-new-password" required type={show ? "text" : "password"} value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} className="clu-input" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono block mb-1.5">{t("cp.confirm")}</span>
            <input data-testid="cp-confirm-password" required type={show ? "text" : "password"} value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} className="clu-input" />
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer select-none py-1">
            <input type="checkbox" data-testid="cp-show-password" checked={show} onChange={(e) => setShow(e.target.checked)} className="w-4 h-4 accent-[#D4AF37]" />
            <span className="text-sm text-white/80 flex items-center gap-1.5">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {t("cp.show")}
            </span>
          </label>
          <button type="submit" data-testid="cp-submit-btn" disabled={busy}
            className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-2.5 rounded-md transition-colors">
            {busy ? t("cp.updating") : t("cp.update")}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}

export function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <button
      data-testid="lang-toggle"
      onClick={() => setLang(lang === "en" ? "it" : "en")}
      className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 hover:bg-white/5 transition-colors flex items-center gap-1.5"
      title="Change language"
    >
      <Globe className="w-3.5 h-3.5" /> {lang === "en" ? "EN" : "IT"}
    </button>
  );
}

export function Logo({ onClick }) {
  return (
    <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group" onClick={onClick}>
      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-[0_4px_16px_rgba(212,175,55,0.25)] group-hover:scale-105 transition-transform">
        <img src="https://customer-assets-0z36b82j.emergentagent.net/job_crypto-legal-hub-1/artifacts/ebf021zt_image.png" alt="LegalUnion" className="w-full h-full object-contain p-0.5" />
      </div>
      <div className="leading-none">
        <span className="font-display font-extrabold text-base sm:text-lg tracking-tight whitespace-nowrap">
          CRYPTO<span className="text-gold">·</span>LEGALUNION
        </span>
      </div>
    </Link>
  );
}

export function SiteHeader() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [showChangePw, setShowChangePw] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 glass-strong border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-3">
          <LangToggle />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="header-admin-link"
                  className="text-sm px-4 py-2 rounded-md border border-white/15 hover:border-gold/50 hover:bg-white/5 transition-colors flex items-center gap-2"
                >
                  <Cog className="w-4 h-4" /> {t("nav.admin")}
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="header-dashboard-link"
                className="text-sm px-4 py-2 rounded-md border border-white/15 hover:border-gold/50 hover:bg-white/5 transition-colors flex items-center gap-2"
              >
                <LayoutDashboard className="w-4 h-4" /> {t("nav.dashboard")}
              </Link>
              <button
                data-testid="header-change-password-btn"
                onClick={() => setShowChangePw(true)}
                className="text-sm px-4 py-2 rounded-md border border-white/15 hover:border-gold/50 hover:bg-white/5 transition-colors flex items-center gap-2"
              >
                <KeyRound className="w-4 h-4" /> {t("nav.changePassword")}
              </button>
              <button
                data-testid="header-logout-btn"
                onClick={onLogout}
                className="text-sm px-4 py-2 rounded-md bg-gold text-sapphire hover:bg-gold-soft transition-colors font-semibold flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" /> {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="text-sm px-4 py-2 rounded-md border border-white/15 hover:border-gold/50 hover:bg-white/5 transition-colors"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                data-testid="header-register-btn"
                className="text-sm px-4 py-2 rounded-md bg-gold text-sapphire hover:bg-gold-soft transition-colors font-semibold"
              >
                {t("nav.signup")}
              </Link>
            </>
          )}
        </div>
      </div>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </header>
  );
}
